import { HeroMinimal, BookingCTA } from "../components/sections";
import { SectionHeader, FeatureCard } from "../components/sections";
import {
  Globe,
  Mail,
  Shield,
  BarChart3,
  Zap,
  RefreshCw,
  Database,
  Send,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";

const infrastructureSections = [
  {
    title: "Domain Configuration",
    description:
      "Dedicated sending domains, subdomain strategy, and reputation isolation to protect primary domain reputation.",
    capabilities: [
      "Primary domain protection",
      "Subdomain architecture",
      "Reputation isolation",
      "Domain warming schedules",
      "DNS configuration",
      "Multi-domain orchestration",
    ],
    icon: Globe,
  },
  {
    title: "Inbox Management",
    description:
      "Rotating inbox pools, automated warm-up schedules, health monitoring, and intelligent distribution across sending infrastructure.",
    capabilities: [
      "Inbox pool rotation",
      "Automated warm-up",
      "Health scoring",
      "Capacity planning",
      "Failure detection",
      "Graceful degradation",
    ],
    icon: Mail,
  },
  {
    title: "Authentication Stack",
    description:
      "Complete SPF, DKIM, DMARC, and BIMI configuration with ongoing compliance monitoring and automated remediation.",
    capabilities: [
      "SPF record management",
      "DKIM key rotation",
      "DMARC policy enforcement",
      "BIMI implementation",
      "Alignment monitoring",
      "Automated alerts",
    ],
    icon: Shield,
  },
  {
    title: "Deliverability Optimization",
    description:
      "Reputation management, spam trap avoidance, placement optimization, and ISP relationship management.",
    capabilities: [
      "Reputation monitoring",
      "Spam trap detection",
      "Inbox placement testing",
      "ISP feedback loops",
      "Seed list testing",
      "Remediation workflows",
    ],
    icon: BarChart3,
  },
  {
    title: "Sending Infrastructure",
    description:
      "Distributed sending architecture with intelligent throttling, capacity management, and failover capabilities.",
    capabilities: [
      "Distributed MTA pools",
      "Intelligent throttling",
      "Capacity auto-scaling",
      "Failover routing",
      "Rate limit compliance",
      "Performance monitoring",
    ],
    icon: Zap,
  },
  {
    title: "Sending Patterns",
    description:
      "Human-like cadence, timezone optimization, volume ramp-up, and behavioral variation to maximize inbox placement.",
    capabilities: [
      "Cadence humanization",
      "Timezone optimization",
      "Volume ramp-up",
      "Behavioral variation",
      "Engagement-based pacing",
      "Adaptive scheduling",
    ],
    icon: RefreshCw,
  },
  {
    title: "Campaign Architecture",
    description:
      "Multi-touch sequence structure, A/B test framework, suppression logic, and compliance guardrails built into the sending layer.",
    capabilities: [
      "Sequence orchestration",
      "A/B test infrastructure",
      "Suppression management",
      "Compliance guardrails",
      "Unsubscribe handling",
      "Frequency capping",
    ],
    icon: Send,
  },
  {
    title: "Data Quality & Hygiene",
    description:
      "Automated bounce management, complaint handling, list hygiene, and continuous data quality enforcement at the infrastructure layer.",
    capabilities: [
      "Bounce classification",
      "Complaint processing",
      "List hygiene automation",
      "Suppression sync",
      "Quality gate enforcement",
      "Feedback loop integration",
    ],
    icon: Database,
  },
];

const infrastructurePrinciples = [
  {
    title: "Infrastructure Before Scale",
    description:
      "Never scale outreach on fragile foundations. Build the plumbing first.",
  },
  {
    title: "Reputation Is an Asset",
    description:
      "Primary domain reputation is irreplaceable. Every decision protects it.",
  },
  {
    title: "Distributed by Design",
    description:
      "No single point of failure. Pools, rotation, and failover built in.",
  },
  {
    title: "Human-Like by Default",
    description:
      "Cadence, timing, and variation mimic natural communication patterns.",
  },
  {
    title: "Observable Everything",
    description:
      "Real-time visibility into health, placement, and performance at every layer.",
  },
  {
    title: "Compliance as Foundation",
    description:
      "CAN-SPAM, GDPR, CASL baked into architecture, not added as afterthought.",
  },
];

const operationalDiscipline = [
  "No purchased lists ever. Only verified, permission-aware data.",
  "Gradual volume ramp-up on new infrastructure (weeks, not days).",
  "Continuous seed testing across major ISPs and filters.",
  "Automated pause on deliverability anomalies.",
  "Monthly infrastructure health reviews and capacity planning.",
  "Dedicated IP pools for high-volume clients with isolation.",
  "Real-time bounce and complaint processing with automatic suppression.",
  "Quarterly authentication audits and key rotation.",
];

function OutboundInfrastructurePage() {
  return (
    <>
      <HeroMinimal
        title="Build the Infrastructure Before You Scale the Outreach."
        subtitle="A serious outbound system requires infrastructure discipline. Domains, inboxes, authentication, deliverability, and sending architecture form the foundation that makes scale possible."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See Data & Prospecting"
        secondaryCtaHref="/technology/data-prospecting"
      />

      {/* Infrastructure Pillars */}
      <section className="section" aria-labelledby="pillars-title">
        <div className="container">
          <SectionHeader
            id="pillars-title"
            eyebrow="Infrastructure Pillars"
            title="Eight Layers of Outbound Infrastructure"
            description="Each layer is a specialized capability. Together they form a resilient, scalable sending foundation."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {infrastructureSections.map((section, index) => (
              <FeatureCard
                key={section.title}
                icon={<section.icon size={24} className="text-accent" />}
                title={section.title}
                description={section.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              />
            ))}
          </div>

          <div className="max-w-5xl mx-auto mt-16 space-y-6">
            {infrastructureSections.map((section, index) => (
              <details
                key={section.title}
                className="group card p-6 animate-slide-up"
                style={{ animationDelay: `${(index + 8) * 50}ms` }}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <div className="flex items-center gap-3">
                    <section.icon
                      size={24}
                      className="text-accent"
                      aria-hidden="true"
                    />
                    <h3 className="h3 text-text-primary">{section.title}</h3>
                  </div>
                  <span className="text-text-muted">Details →</span>
                </summary>
                <div className="mt-4 pt-4 border-t border-border grid md:grid-cols-2 gap-3 animate-slide-down">
                  {section.capabilities.map((cap, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-text-secondary"
                    >
                      <CheckCircle
                        size={14}
                        className="text-accent flex-shrink-0"
                        aria-hidden="true"
                      />
                      {cap}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="principles-title"
      >
        <div className="container">
          <SectionHeader
            id="principles-title"
            eyebrow="Principles"
            title="Infrastructure Discipline"
            description="Six principles that guide every infrastructure decision. They are what makes the system reliable."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {infrastructurePrinciples.map((principle, index) => (
              <div
                key={principle.title}
                className="card-elevated p-6 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="h3 text-text-primary mb-2">{principle.title}</h3>
                <p className="body text-text-secondary">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operational Discipline */}
      <section className="section" aria-labelledby="discipline-title">
        <div className="container">
          <SectionHeader
            id="discipline-title"
            eyebrow="Operational Standards"
            title="What We Don't Do"
            description="Infrastructure discipline is as much about what you refuse to do as what you do."
            align="center"
          />
          <div className="max-w-3xl mx-auto mt-12">
            <ul className="space-y-4" role="list">
              {operationalDiscipline.map((item, index) => (
                <li
                  key={index}
                  className="card p-5 flex items-start gap-4 animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <AlertTriangle
                    size={20}
                    className="text-warning flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <p className="body text-text-secondary">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Transparency Note */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="transparency-title"
      >
        <div className="container">
          <SectionHeader
            id="transparency-title"
            eyebrow="Transparency"
            title="Competence Without Exposing Operations"
            description="We demonstrate technical competence without publishing operational details that would expose sensitive internal practices or enable abuse."
            align="center"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {[
              {
                title: "No Guaranteed Placement",
                description:
                  "We don't promise inbox placement. We build infrastructure that earns it.",
              },
              {
                title: "No Secret Sauce Claims",
                description:
                  "Our methods are industry best practices, executed with discipline and consistency.",
              },
              {
                title: "No Abuse Enablement",
                description:
                  "We don't publish configurations that could be misused. Security through obscurity where appropriate.",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="card-elevated p-6 text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="h3 text-text-primary mb-2">{item.title}</h3>
                <p className="body text-text-secondary">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default OutboundInfrastructurePage;
